import { useEffect, useRef, useState } from "react";

import api from "@/services/api";

interface Partnership {
  id: number;
  logo_url: string;
  organization_name: string;
  partnership_type: string;
}

export default function OpportunitiesSlider() {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    const fetchPartnerships = async () => {
      try {
        setLoading(true);
        const response = await api.get("/partnerships");
        if (response.data.success && response.data.partnerships) {
          setPartnerships(response.data.partnerships);
        }
      } catch (err) {
        console.error("Failed to fetch partnerships:", err);
        setError("Failed to load partnerships");
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerships();
  }, []);

  // Simple scroll by 1 card left/right
  const scroll = (dir: "left" | "right") => {
    if (!sliderRef.current) return;
    const card = sliderRef.current.querySelector(".partnership-card");
    if (!card) return;
    const cardWidth = (card as HTMLElement).offsetWidth + 16; // 16px gap
    sliderRef.current.scrollBy({
      behavior: "smooth",
      left: dir === "left" ? -cardWidth : cardWidth,
    });
  };

  return (
    <section className="relative mx-auto w-full max-w-7xl py-8">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-3 inline-block">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            </div>
            <p className="text-base-content/60">Loading partnerships...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-error/10 p-4 text-center text-error">
          {error}
        </div>
      )}

      {!loading && !error && partnerships.length >= 4 && (
        <div className="flex items-center">
          <button
            aria-label="Previous"
            className="mr-2 hidden rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white sm:inline-flex"
            onClick={() => scroll("left")}
            type="button"
          >
            <svg height={28} viewBox="0 0 24 24" width={28}>
              <path
                d="M15 19l-7-7 7-7"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
          <div
            className="flex gap-4 overflow-x-auto scroll-smooth pb-2"
            ref={sliderRef}
            style={{ scrollbarWidth: "none" }}
          >
            {partnerships.map((partnership) => (
              <div
                className="partnership-card flex min-w-[260px] max-w-xs flex-shrink-0 flex-col items-center justify-center rounded-2xl bg-base-200 p-4 shadow-lg dark:bg-base-300"
                key={partnership.id}
              >
                <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-lg bg-base-100 dark:bg-base-200">
                  <img
                    alt={partnership.organization_name}
                    className="h-full w-full object-contain p-1"
                    src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${partnership.logo_url}`}
                  />
                </div>
                <div className="mb-1 break-words text-center text-lg font-bold text-base-content">
                  {partnership.organization_name}
                </div>
                <div className="text-center text-sm text-base-content opacity-70">
                  {partnership.partnership_type}
                </div>
              </div>
            ))}
          </div>
          <button
            aria-label="Next"
            className="ml-2 hidden rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white sm:inline-flex"
            onClick={() => scroll("right")}
            type="button"
          >
            <svg height={28} viewBox="0 0 24 24" width={28}>
              <path
                d="M9 5l7 7-7 7"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}
